import { Box, Icon, Link, Spinner } from '@amzn/awsui-components-core';
import { Attachment, AttachmentScanResult } from '@amzn/awscat-pattern-library-service-model';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';
import '../../resources/styles/scss/components/common/RenderAttachments.css';

interface Props {
  contentId: string;
  attachmentId: string;
  readOnly: boolean;
  onDelete: (attachmentId: string) => void;
  onUpdate: (attachment: Attachment) => void;
}

export const AttachmentListItem: FunctionComponent<Props> = (props: Props) => {
  const { contentId, attachmentId } = props;

  const [attachment, setAttachment] = useState<Attachment>();
  const [loadingError, setLoadingError] = useState(false);
  let attachmentUpdatePolling: any;

  const mplService = MplServiceClientFactory.create();
  const componentIsMounted = useRef(true);

  useEffect(() => {
    componentIsMounted.current = true;
    if (!loadingError && !attachment) {
      updateAttachmentInfo();
    }
    return () => {
      if (attachmentUpdatePolling) {
        clearInterval(attachmentUpdatePolling);
      }
      componentIsMounted.current = false;
    };
  });

  const updateAttachmentInfo = async () => {
    try {
      const { attachment: updatedAttachmentInfo } = await mplService.getAttachmentInfo(
        contentId,
        attachmentId,
      );
      if (componentIsMounted.current) {
        setAttachment(updatedAttachmentInfo);
      }

      if (!updatedAttachmentInfo.scanResult) {
        pollUpdateAttachment();
      }

      if (componentIsMounted.current && props.onUpdate) {
        props.onUpdate(updatedAttachmentInfo);
      }
    } catch (e) {
      // @ts-expect-error TS18046 - strict migration
      if (e.response && e.response.status === 404) {
        props.onDelete(attachmentId);
        if (attachmentUpdatePolling) {
          clearInterval(attachmentUpdatePolling);
        }
        setLoadingError(true);
      }
    }
  };

  const deleteFile = async () => {
    if (props.onDelete) {
      props.onDelete(attachmentId);
    }
  };

  const pollUpdateAttachment = async () => {
    attachmentUpdatePolling = setTimeout(async () => {
      await updateAttachmentInfo();
    }, 5000);
  };

  const renderStatusIndicator = () => {
    // @ts-expect-error TS18048 - strict migration
    if (!attachment.scanResult) {
      return (
        <span className={'attachment-scan-result-indicator'}>
          <Spinner />
        </span>
      );
    } else {
      // @ts-expect-error TS18048 - strict migration
      switch (attachment.scanResult) {
        case AttachmentScanResult.OK:
          return (
            <span className={'attachment-scan-result-indicator'}>
              <Icon name={'status-positive'} />
            </span>
          );
        case AttachmentScanResult.INFECTED:
          return (
            <span
              className={'attachment-scan-result-indicator'}
              title={constants.ATTACHMENTS_INFECTED_INFO}
            >
              <Icon name={'status-warning'} variant={'warning'} />
            </span>
          );
      }
    }
  };

  const renderFilename = () => {
    // @ts-expect-error TS18048 - strict migration
    if (attachment.scanResult === AttachmentScanResult.OK) {
      return (
        <Box display="inline" padding="s">
          {/* @ts-expect-error TS18048 - strict migration */}
          <Link variant="secondary" href={attachment.url}>
            {/* @ts-expect-error TS18048 - strict migration */}
            {decodeURIComponent(attachment.filename)}
          </Link>
        </Box>
      );
    } else {
      return (
        <Box display="inline" padding="s">
          {/* @ts-expect-error TS18048 - strict migration */}
          {decodeURIComponent(attachment.filename)}
        </Box>
      );
    }
  };

  // @ts-expect-error TS7030 - strict migration
  const renderDeleteButton = () => {
    const { readOnly } = props;
    if (!readOnly) {
      return (
        <span className={'attachment-delete-button'} onClick={deleteFile}>
          <Icon size="normal" variant="normal" name="close" alt={constants.DELETE_ATTACHMENT_ALT} />
        </span>
      );
    }
  };

  if (loadingError) {
    return (
      <div>
        <span>Error loading attachment with ID {attachmentId}</span>
        {renderDeleteButton()}
      </div>
    );
  }

  if (!attachment) {
    return null;
  }

  return (
    <div>
      {renderStatusIndicator()}
      {renderFilename()}
      {renderDeleteButton()}
    </div>
  );
};
